import React from 'react';
import PropTypes from 'prop-types';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Widget from 'components/Widget';
import PlaceholderSpinner from 'components/kea/placeholders/PlaceholderSpinner';
import PlaceholderError from 'components/kea/placeholders/PlaceholderError';
import { analyticsChartTheme, formatCompactMetric } from 'util/chartUtils';
import styles from './AnalyticsTrendChart.module.scss';
import IntlMessages from 'util/IntlMessages';

const TONES = Object.freeze({
  searches: { stroke: analyticsChartTheme.searches, fill: analyticsChartTheme.searchesFill },
  clickRate: { stroke: analyticsChartTheme.clickRate, fill: analyticsChartTheme.clickRateFill },
  zeroResults: { stroke: analyticsChartTheme.zeroResults, fill: analyticsChartTheme.zeroResultsFill },
  clickEvents: { stroke: analyticsChartTheme.clickEvents, fill: analyticsChartTheme.clickEventsFill },
});

export const TrendTooltip = ({ active, payload, label, seriesLabel, valueFormatter, tone }) => {
  if (!active || !payload || !payload.length) return null;
  const date = payload[0]?.payload?.label || label;
  return (
    <div className={styles.tooltip}>
      <p className={styles.tooltipLabel}>{date}</p>
      <div className={styles.tooltipRow}>
        <span className={styles.tooltipName}>
          <span
            className={styles.tooltipDot}
            style={{ backgroundColor: TONES[tone].stroke }}
            aria-hidden="true"
          />
          {seriesLabel}
        </span>
        <span className={styles.tooltipValue}>{valueFormatter(payload[0].value)}</span>
      </div>
    </div>
  );
};

TrendTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    payload: PropTypes.shape({ label: PropTypes.string }),
  })),
  label: PropTypes.string,
  seriesLabel: PropTypes.string.isRequired,
  valueFormatter: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(Object.keys(TONES)).isRequired,
};

TrendTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

export default function AnalyticsTrendChart({
  data,
  dataKey,
  seriesLabel,
  title,
  subtitle,
  noDataLabel,
  valueFormatter,
  axisFormatter,
  tone,
  isFetching,
  error,
}) {
  const hasData = data.length > 0;
  const colors = TONES[tone];
  const gradientId = `analytics-trend-${tone}`;

  return (
    <Widget styleName={`jr-card-full noMargin ${styles.card}`}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        <span className={styles.seriesLabel}>
          <span style={{ backgroundColor: colors.start }} aria-hidden="true" />
          {seriesLabel}
        </span>
      </div>

      {isFetching && <PlaceholderSpinner height={326} />}
      {!isFetching && error && <PlaceholderError error={error} height={326} />}
      {!isFetching && !error && !hasData && (
        <div className={styles.emptyState}>{noDataLabel}</div>
      )}
      {!isFetching && !error && hasData && (
        <>
          <div
            className={styles.chartFrame}
            role="img"
            aria-label={`${title}. ${subtitle}`}
            data-testid="analytics-selected-trend"
            data-metric={dataKey}
          >
            <ResponsiveContainer width="100%" height={326}>
              <AreaChart data={data} margin={{ top: 18, right: 20, left: 0, bottom: 8 }}>
                <defs>
                  <linearGradient id={`${gradientId}-fill`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.fill} stopOpacity={1} />
                    <stop offset="60%" stopColor={colors.fill} stopOpacity={0.48} />
                    <stop offset="100%" stopColor={colors.fill} stopOpacity={0.08} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="2 7"
                  vertical={false}
                  stroke={analyticsChartTheme.grid}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: analyticsChartTheme.muted }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={32}
                  dy={8}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: analyticsChartTheme.muted }}
                  tickFormatter={axisFormatter}
                  axisLine={false}
                  tickLine={false}
                  width={46}
                  dx={-4}
                />
                <Tooltip
                  cursor={{ stroke: analyticsChartTheme.border, strokeWidth: 1, strokeDasharray: '3 5' }}
                  content={(
                    <TrendTooltip
                      seriesLabel={seriesLabel}
                      valueFormatter={valueFormatter}
                      tone={tone}
                    />
                  )}
                />
                <Area
                  dataKey={dataKey}
                  type="monotone"
                  stroke={colors.stroke}
                  strokeWidth={2.5}
                  fill={`url(#${gradientId}-fill)`}
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 5, fill: colors.stroke, stroke: analyticsChartTheme.surface, strokeWidth: 3 }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <table className={styles.visuallyHidden}>
            <caption>{`${title}. ${subtitle}`}</caption>
            <thead>
              <tr>
                <th scope="col"><IntlMessages id="ui.search.analytic.healthDashboard.analyticsTrendChart.date" /></th>
                <th scope="col">{seriesLabel}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  <td>{valueFormatter(row[dataKey])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Widget>
  );
}

AnalyticsTrendChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string })).isRequired,
  dataKey: PropTypes.string.isRequired,
  seriesLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  noDataLabel: PropTypes.string.isRequired,
  valueFormatter: PropTypes.func,
  axisFormatter: PropTypes.func,
  tone: PropTypes.oneOf(Object.keys(TONES)),
  isFetching: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
};

AnalyticsTrendChart.defaultProps = {
  valueFormatter: (value) => Number(value).toLocaleString(),
  axisFormatter: formatCompactMetric,
  tone: 'searches',
  isFetching: false,
  error: null,
};
